import { useEffect } from 'react';

import { isDesktop } from '@/consts/device';
import { getAppStore, setAppStore } from '@/store';
import {
  loadGenerations,
  clearGeneration,
} from '@/utils/storage/generationStorage';
import { getMediaFromFlowId } from '@/requests/magic-flow/getMediaFromFlowId';
import { changeDriveFileName } from '@/requests/cloud-storage/changeDriveFileName';
import { getExtensionFromResultType } from '@/hooks/useGenerate/utils';
import { createContentFile } from '@/utils/media/createContentFile';
import { extractUrlParams } from '@/utils/ui/extractUrlParams';
import { DeeplinkKeys } from '@/consts/deepLinks';

import type { ContentFile, PreviewMediaType } from '@/types';
import type { PersistedGeneration } from '@/utils/storage/generationStorage';

// Module-level flag to prevent double-execution in React Strict Mode
let hasAttemptedRecovery = false;

const recoverGeneration = async (persisted: PersistedGeneration) => {
  const { generationId, placeholderUuid, presetTitle } = persisted;

  // Restore placeholder with per-card progress data
  const placeholder: ContentFile = {
    uuid: placeholderUuid,
    name: 'Generating...',
    url: '',
    isLoading: true,
    generationStartTime: persisted.startedAt,
    presetType: persisted.presetType as PreviewMediaType | undefined,
  };
  getAppStore().prependContentFile(placeholder);

  try {
    const media = await getMediaFromFlowId(generationId);

    if (!media) {
      // eslint-disable-next-line no-console
      console.log(
        '[GenerationRecovery] No result from recovered generation',
        generationId
      );
      getAppStore().showToast('Generation failed', 'error');
      const { contentFiles, setContentFiles } = getAppStore();
      setContentFiles(contentFiles.filter((f) => f.uuid !== placeholderUuid));
      return;
    }

    const { contentFile } = createContentFile({
      media,
      fileName: `${presetTitle || 'Generated'} - recovered`,
    });

    // Rename on Drive with a proper filename
    if (media.driveUid) {
      const extension = getExtensionFromResultType(media.type.toLowerCase());
      const filename = `${presetTitle || 'Generated'}.${extension}`;
      try {
        await changeDriveFileName({ id: media.driveUid, name: filename });
      } catch {
        // Non-critical
      }
    }

    // Update placeholder with real data
    getAppStore().updateContentFile({
      uuid: placeholderUuid,
      updates: {
        ...contentFile,
        isLoading: false,
      },
    });

    // Show notification and open lightbox for the last recovered generation
    setAppStore({
      resultFile: media,
      generationTimestamp: Date.now(),
      pendingLightboxFileId: contentFile.uuid,
      isResultMode: true,
    });

    getAppStore().setLocalNotification(media);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(
      '[GenerationRecovery] Failed to recover generation:',
      generationId,
      error
    );
    getAppStore().showToast('Generation failed', 'error');
    const { contentFiles, setContentFiles } = getAppStore();
    setContentFiles(contentFiles.filter((f) => f.uuid !== placeholderUuid));
  } finally {
    await clearGeneration(generationId);
  }
};

const useGenerationRecovery = () => {
  useEffect(() => {
    if (hasAttemptedRecovery) return;
    hasAttemptedRecovery = true;

    // Skip if already generating (normal flow is active)
    if (getAppStore().isGenerating) return;

    // Skip if taskId deeplink exists (notification handler takes priority)
    const urlParams = extractUrlParams([DeeplinkKeys.taskId]);
    if (urlParams[DeeplinkKeys.taskId]) return;

    const persisted = loadGenerations();
    if (persisted.length === 0) return;

    const recoverAll = async () => {
      setAppStore({ isGenerating: true });

      // Navigate to creations
      if (isDesktop) {
        setAppStore({ rightPanelTab: 'creations' });
      } else {
        setAppStore({ screen: 'files' });
      }

      // Recover all generations in parallel
      await Promise.all(persisted.map(recoverGeneration));

      setAppStore({ isGenerating: false });
    };

    recoverAll().catch(() => {});
  }, []);
};

export { useGenerationRecovery };
